import type { ReviewCard, ReviewMode } from '@/api/review'

const STORAGE_KEY = 'lexio-review-session'

/**
 * Keep cards that match the mode the user selected.
 * MC / fill-blank must not show Classic phrase fallbacks.
 */
export function cardsMatchingSelectedMode(
  cards: ReviewCard[],
  selectedMode: ReviewMode,
): ReviewCard[] {
  if (selectedMode === 'CLASSIC' || selectedMode === 'REVERSE') {
    return cards
  }
  return cards.filter((card) => card.mode === selectedMode)
}

export interface PersistedReviewSession {
  sessionId: string
  cards: ReviewCard[]
  currentIndex: number
  correctCount: number
  ratingsSubmitted: number
  /**
   * Start of the current *visible* timing segment.
   * On restore / tab-hide, this is reset so idle wall-clock is not credited.
   */
  sessionStartedAt: number
  /** Visible study ms accumulated before the current segment. */
  activeStudyMs?: number
  /** Native language when the session was saved — used to discard stale queues after language switch */
  nativeLanguage?: string
  /** Card IDs rated "Again" — re-appended to queue on next page load */
  pendingAgainCardIds: string[]
  /** @deprecated legacy field from older sessions */
  pendingAgainWordIds?: string[]
}

/** True when saved cards belong to a different vocabulary language or no longer exist in the API queue */
export function isReviewSessionStale(
  saved: PersistedReviewSession,
  dueCards: ReviewCard[],
  nativeLanguage?: string,
): boolean {
  if (nativeLanguage && saved.nativeLanguage && saved.nativeLanguage !== nativeLanguage) {
    return true
  }
  if (dueCards.length === 0) {
    return saved.cards.length > 0
  }
  const dueCardIds = new Set(dueCards.map((c) => c.id))
  return saved.cards.some((card) => !dueCardIds.has(card.id))
}

/** Keep only cards that still exist in the current-language refresh queue */
export function filterCardsForDueQueue(cards: ReviewCard[], dueCards: ReviewCard[]): ReviewCard[] {
  const dueById = new Map(dueCards.map((c) => [c.id, c]))
  return cards
    .filter((card) => dueById.has(card.id))
    .map((card) => dueById.get(card.id) ?? card)
}

export function loadReviewSession(): PersistedReviewSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PersistedReviewSession
    if (!parsed.sessionId || !Array.isArray(parsed.cards)) return null
    return {
      ...parsed,
      pendingAgainCardIds: parsed.pendingAgainCardIds ?? parsed.pendingAgainWordIds ?? [],
    }
  } catch {
    return null
  }
}

export function saveReviewSession(session: PersistedReviewSession): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session))
  } catch {
    // ignore quota errors
  }
}

export function clearReviewSession(): void {
  sessionStorage.removeItem(STORAGE_KEY)
}

/** Append due cards for pending "Again" cards that are not ahead in the queue */
export function mergePendingAgainCards(
  cards: ReviewCard[],
  currentIndex: number,
  pendingAgainCardIds: string[],
  dueCards: ReviewCard[],
): { cards: ReviewCard[]; pendingAgainCardIds: string[] } {
  if (pendingAgainCardIds.length === 0) {
    return { cards, pendingAgainCardIds }
  }

  const aheadIds = new Set(cards.slice(currentIndex).map((c) => c.id))
  const dueById = new Map(dueCards.map((c) => [c.id, c]))
  const stillPending: string[] = []
  const toAppend: ReviewCard[] = []

  for (const cardId of pendingAgainCardIds) {
    if (aheadIds.has(cardId)) {
      stillPending.push(cardId)
      continue
    }
    const dueCard = dueById.get(cardId)
    if (dueCard) {
      toAppend.push(dueCard)
      stillPending.push(cardId)
    }
  }

  if (toAppend.length === 0) {
    return { cards, pendingAgainCardIds: stillPending }
  }

  return {
    cards: [...cards, ...toAppend],
    pendingAgainCardIds: stillPending,
  }
}

export function dedupeCardsById(cards: ReviewCard[]): ReviewCard[] {
  const seen = new Set<string>()
  const result: ReviewCard[] = []
  for (const card of cards) {
    if (seen.has(card.id)) continue
    seen.add(card.id)
    result.push(card)
  }
  return result
}

/** @deprecated use dedupeCardsById */
export function dedupeCardsByWordId(cards: ReviewCard[]): ReviewCard[] {
  return dedupeCardsById(cards)
}
